import * as React from "react";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { styled } from "@mui/material";
import { QualifiedFeature } from "fe-common/graphql";
import { PatientColorMap } from "../../layout/patientColorMap";
import { DocQuestion } from "./DocQuestion";
import { QuestionOptionsContainer } from "./layout/questionOptionsContainer";
import { GenericButton } from "../uiControllers/genericButton";
import { useTranslate } from "../../hooks/useTranslate";
import { MultipleQuestionOptionChip } from "./multipleQuestionOptionChip";
import { scrollableStyleByColor } from "fe-common/ui/commonStyles";
import { usePatientFeatureFlags } from "../../hooks/usePatientFeatureFlags";

//region [[ Styles ]]

const ClosedMultipleQuestionViewContainer = styled("div")({
  width: "100%",
});

const AnswersContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const TopScrollableArea = styled("div")({
  display: "flex",
  flexFlow: "column",
  width: "100%",
  maxHeight: "60vh",
  ...scrollableStyleByColor(PatientColorMap.PrimaryLight),
});

export const QuestionButtonArea = styled("div")({
  alignItems: "center",
  "& .submit-button": {
    "&:active, &:hover, &": {
      color: PatientColorMap.Default,
      fontWeight: 600,
      backgroundColor: PatientColorMap.Unselected,
    },
    color: PatientColorMap.Default,
  },
  display: "flex",
  justifyContent: "center",
  padding: "10px",
});
//endregion [[ Styles ]]

//region [[ Props ]]
export enum RenderChildrenAt {
  Bottom,
  Top,
}

export interface ClosedMultipleQuestionViewProps {
  suggestions: QualifiedFeature[];
  onDone: (selected: Map<string, [QualifiedFeature, boolean]>) => void;
  className?: string;
  answer?: QualifiedFeature[];
  questionsText?: string;
  labeler?: (qualifiedFeature: QualifiedFeature) => React.ReactNode;
  cfaText?: string; // Call for action text default - "none of the above"
  onCfa?: () => void; //What will happen when the call for action will be clicked?
  maxItems?: number; // should the number of options be limited
  children?: React.ReactNode;
  preventSkip?: boolean;
  // If passed the component will change from state full to controlled
  controlledSuggestions?: Map<string, [QualifiedFeature, boolean]>;
  // If controlled, every selected item will trigger this event
  onSelected?: (qualifiedFeature: QualifiedFeature) => void;
  singleSelect?: boolean;
  // If set to true feature description will hide for all options even if it's present
  hideDescription?: boolean;
  // Weather there is reasoning for this question or not
  basedOnReasoning?: boolean;
  // Was this asked a background condition? we need the info to be able to get proper reasoning in debug mode
  isBackgroundQuestion?: boolean;
  // We can render a child component (usually for search) at top or bottom (default: bottom)
  renderChildrenAt?: RenderChildrenAt;
  // Used for when you want to render the question in not a chat-question view (like we do in medication frequency direction)
  hideQuestion?: boolean;
  // miniView is used for form quesion, where the quesion has smaller text and non-chat like view
  miniView?: boolean;
  // Provides an option to customize "None of the above" text
  skipButtonOverrideText?: string;
  // When turned to true, if the list chnages the last option is being scrolled to
  scrollToAdded?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]
/****
 ********** This component is a dumb view only! *********
 *   *** Do not add any external business logic here ****
 *
 * This components creates a view of a multiple selection chat bot
 * question allowing to choose from a closed set of qualified features
 * and also allows rending custom made components as children within the selection area
 * @param props = {
 *    suggestions: QualifiedFeature[]; -- List of possible values to choose from
 *    answer?:QualifiedFeature[] -- Items selected / null if should be prompted
 *    questionsText?: string -- The text which will be displayed next to the questions
 *    children?:
 *  }
 * @constructor
 */
export const ClosedMultipleQuestionView = forwardRef<
  HTMLDivElement,
  ClosedMultipleQuestionViewProps
>(
  (
    {
      onSelected,
      suggestions,
      singleSelect,
      onDone,
      renderChildrenAt = RenderChildrenAt.Bottom,
      hideQuestion = false,
      scrollToAdded = false,
      ...props
    },
    ref,
  ) => {
    const isAnswered = !!props.answer;

    const [selected, setSelected] = useState<
      Map<string, [QualifiedFeature, boolean]>
    >();

    const { translate } = useTranslate();
    const scrollToLastAnswer = usePatientFeatureFlags("scrollToLastAnswerOnly");

    useEffect(() => {
      // If controlled make sure to update the current state on each update of props
      if (props.controlledSuggestions) {
        setSelected(props.controlledSuggestions);
        return;
      }
      // If state full initialize state to all false every time we get new suggestions or answer
      const initSelected = new Map();

      suggestions.forEach((s) => initSelected.set(s.id, [s, false]));

      setSelected(initSelected);
    }, [suggestions, props.answer, props.controlledSuggestions]);

    const selectedNum = selected
      ? Array.from(selected.values()).filter(
          ([selectedFeature, isSelected]) => isSelected,
        ).length
      : 0;

    const featureSelectionToggle = useCallback(
      (qFeature) => {
        // If controlled update the parent and let state update from props
        if (props.controlledSuggestions && onSelected) {
          onSelected(qFeature);
          return;
        }

        if (singleSelect) {
          onDone(new Map([[qFeature.id, [qFeature, true]]]));
          return;
        }

        // State full mode - Create a new updated map to re-render the component
        setSelected((olderSelectedMap) => {
          const isSelected = olderSelectedMap.get(qFeature.id)[1];
          return new Map([
            ...olderSelectedMap,
            [qFeature.id, [qFeature, !isSelected]],
          ]);
        });
      },
      [
        setSelected,
        props.controlledSuggestions,
        onSelected,
        singleSelect,
        onDone,
      ],
    );

    const lastItemRef = useRef<HTMLDivElement>();
    const firstItemRef = useRef<HTMLDivElement>();

    // Scroll to the last item (used for when user added a new item from search)
    useEffect(() => {
      if (lastItemRef.current && !scrollToLastAnswer) {
        setTimeout(
          () =>
            lastItemRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "start",
            }),
          300,
        );
      }
      if ((firstItemRef.current || lastItemRef.current) && scrollToAdded) {
        setTimeout(
          () =>
            (firstItemRef.current || lastItemRef.current).scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "start",
            }),
          500,
        );
      }
    }, [suggestions, scrollToLastAnswer, scrollToAdded]);

    const buttonText = selectedNum
      ? "ANSWER_DONE"
      : props.cfaText ||
        (props.preventSkip
          ? "ANSWER_DONE"
          : props.skipButtonOverrideText || "ANSWER_NONE_OF_THE_ABOVE");

    return (
      <ClosedMultipleQuestionViewContainer
        className={(props.className || "") + " closed-multiple-question-view"}
        ref={ref}
      >
        {props.questionsText && !hideQuestion && (
          <DocQuestion text={props.questionsText} miniView={props.miniView} />
        )}
        {!isAnswered && (
          <QuestionOptionsContainer className={"question-options-container"}>
            <AnswersContainer className={"answers-container"}>
              {renderChildrenAt === RenderChildrenAt.Top && props.children}
              <TopScrollableArea>
                {suggestions
                  .slice(0, props.maxItems || suggestions.length)
                  .map((qualifiedFeature, index) => {
                    const isLast =
                      index === (props.maxItems || suggestions.length - 1);
                    const isFirst = index === 0;
                    const isSelected =
                      (selected
                        ?.get(qualifiedFeature.id)
                        ?.slice(-1)
                        ?.pop() as boolean) || false;
                    return (
                      <MultipleQuestionOptionChip
                        key={qualifiedFeature?.id || index}
                        qualifiedFeature={qualifiedFeature}
                        labeler={props.labeler}
                        onClick={featureSelectionToggle}
                        selected={isSelected}
                        disabled={singleSelect && !isSelected && !!selectedNum}
                        questionText={props.questionsText}
                        hideDescription={props.hideDescription}
                        basedOnReasoning={props.basedOnReasoning}
                        isBackground={props.isBackgroundQuestion}
                        ref={
                          isLast && selected
                            ? lastItemRef
                            : isFirst
                            ? firstItemRef
                            : null
                        }
                      />
                    );
                  })}
              </TopScrollableArea>
              {renderChildrenAt === RenderChildrenAt.Bottom && props.children}
            </AnswersContainer>
            {!singleSelect && (
              <QuestionButtonArea>
                <GenericButton
                  className={"submit-button"}
                  label={translate(buttonText)}
                  // If nothing was selected try to call the call-for-action if otherwise call done with the selected list
                  onClick={
                    (!selectedNum && props.onCfa) || (() => onDone(selected))
                  }
                  // If we can't skip and no item was yet selected and there is no call-for-action - disable the button
                  disabled={props.preventSkip && !selectedNum && !props.onCfa}
                  skipAnalytics
                />
              </QuestionButtonArea>
            )}
          </QuestionOptionsContainer>
        )}
      </ClosedMultipleQuestionViewContainer>
    );
  },
);
